import { useQueryClient } from "@tanstack/react-query";
import { Realtime } from "ably";
import { useEffect, useRef } from "react";
import type { OrderChangeEvent } from "@/api/vendure/order/order.types";

export type { OrderChangeEvent };

const ABLY_ORDER_EVENT = "order_change";

export function buildChannelName(
  customerId: string,
  appEnv = import.meta.env.VITE_APP_ENV as string,
): string {
  return `customer.${customerId}.${appEnv ?? "development"}`;
}

export function useOrderRealtime(
  orderCode: string,
  customerId: string | undefined,
  onStatusChange?: (event: OrderChangeEvent) => void,
) {
  const queryClient = useQueryClient();
  const callbackRef = useRef(onStatusChange);
  callbackRef.current = onStatusChange;

  useEffect(() => {
    const apiKey = import.meta.env.VITE_ABLY_API_KEY as string | undefined;
    if (!apiKey || !customerId || !orderCode) return;

    const client = new Realtime({
      key: apiKey,
      clientId: customerId,
      logLevel: 1,
    });
    const channel = client.channels.get(buildChannelName(customerId));

    channel.subscribe(ABLY_ORDER_EVENT, (message) => {
      const event = message.data as OrderChangeEvent;
      if (event.orderId !== orderCode) return;
      queryClient.invalidateQueries({ queryKey: ["order-detail", orderCode] });
      callbackRef.current?.(event);
    });

    return () => {
      channel.unsubscribe();
      client.close();
    };
  }, [orderCode, customerId, queryClient]);
}
